import { Injectable } from '@angular/core';
import { Utils } from 'weavix-shared/utils/utils';
import { BehaviorSubject } from 'rxjs';
import { WebMqttStorage } from './web-mqtt-storage';
import { environment } from '../../environments/environment';
import { Topic } from '@weavix/models/src/topic/topic';
import { HttpService } from './http.service';
import { MqttService } from '@weavix/mqtt/src/mqtt-service';
import { mqttTopicReplace } from '@weavix/mqtt/src/utils/mqtt-topic-replace';
import { MqttQos } from '@weavix/mqtt/src/models/mqtt-qos';
import { MqttTransformationType } from '@weavix/mqtt/src/models/mqtt-transformation-type';
import { ObservableSubscribe } from '@weavix/mqtt/src/models/observable-subscribe';
import { TopicPayload } from '@weavix/mqtt/src/models/topic-payload';
import { subscribeWithReplay as mqttSubscribeWithReplay } from '@weavix/mqtt/src/utils/subscribe-with-replay';
import { subscribeWithReconnect } from '@weavix/mqtt/src/utils/subscribe-with-reconnect';
// shared worker connection
// import { getWorker } from '../workers/shared.client';

@Injectable({
    providedIn: 'root',
})
export class PubSubService {
    private internalClient: MqttService;
    private readonly storage = new WebMqttStorage('outgoing');
    private options: any = {};
    private connecting = false;
    // test tokens
    static token = '';

    connected$ = new BehaviorSubject<boolean>(false);

    constructor() {
        this.initInternalClient();
    }

    get clientId() {
        return this.internalClient.clientId;
    }

    async disconnect() {
        const oldClient = this.internalClient;
        this.internalClient = null;
        this.connecting = false;
        oldClient?.disconnect();
    }

    async loggedIn() {
        const oldClient = this.internalClient;
        if (!this.initInternalClient()) {
            if (!this.connecting) {
                this.internalClient.connect();
                this.connecting = true;
            }
            return;
        }
        this.internalClient.connect();
        this.connecting = true;
        oldClient?.disconnect();
    }

    private initInternalClient() {
        // auth happens via cookie on web so we can pass an empty string here for the auth token
        const platform = environment.teamsApp ? 'teams' : environment.radioApp ? 'web-radio' : 'console';
        const options = {
            // connect: getWorker().connect as any,
            url: environment.pubsubApi,
            port: environment.pubsubPort,
            platform,
            source: `${platform}-${environment.version}`,
            storage: this.storage,
            token: environment.teamsApp && HttpService.bearerToken ? HttpService.bearerToken : PubSubService.token,
            timeout: 30,
            ssl: !!environment.is360Api.includes('https://'),
        };
        if (this.internalClient && Object.keys(options).every(key => options[key] === this.options[key])) return false;
        this.options = options;
        this.internalClient = new MqttService(options);
        this.internalClient.connected$.subscribe(val => {
            if (val !== this.connected$.value) this.connected$.next(val);
        });
        return true;
    }

    public async publish(topic: Topic, args: string[], payload: any, qos: MqttQos = MqttQos.atLeastOnce) {
        const resolvedTopic = mqttTopicReplace(topic, args);

        const blob = payload instanceof Uint8Array ? payload : payload instanceof Blob ? await (payload as any).arrayBuffer() : JSON.stringify(payload);

        this.internalClient.publishRaw(resolvedTopic, blob, qos);
    }

    public subscribe<T>(component: any, topic: Topic, args: string[], retain: boolean | number = true, qos: MqttQos = MqttQos.atLeastOnce, raw: boolean = false): ObservableSubscribe<TopicPayload<T>> {
        const resolvedTopic = mqttTopicReplace(topic, args);
        const transform = raw ? MqttTransformationType.raw : MqttTransformationType.json;

        const obs = this.subscribeInternal<T>(resolvedTopic, retain, qos, transform);

        const safeObs = Utils.safeSubscribe(component, obs);
        return Object.assign(safeObs, { subscribed: obs.subscribed });
    }

    // pass through methods must throw errors when disconnected whereas normal subscribe methods simply reconnect
    public subscribePassThrough<T>(topic: string): ObservableSubscribe<TopicPayload<T>> {
        return this.internalClient.subscribe(topic);
    }

    private subscribeInternal<T>(resolvedTopic: string, retain: boolean | number, qos: MqttQos, transform: MqttTransformationType): ObservableSubscribe<TopicPayload<T>> {
        if (!retain) {
            return subscribeWithReconnect<T>(this.internalClient, resolvedTopic, qos as any, transform);
        } else {
            const bufferSize = (retain === true || retain === 1) ? 1 : undefined; // undefined === infinite
            return this.subscribeWithReplay(resolvedTopic, bufferSize, qos, transform);
        }
    }

    private subscribeWithReplay<T>(topicString: string, bufferSize: number | undefined, qos: MqttQos, transform: MqttTransformationType): ObservableSubscribe<TopicPayload<T>> {
        return mqttSubscribeWithReplay(topicString, bufferSize, () => {
            return subscribeWithReconnect(this.internalClient, topicString, qos, transform);
        });
    }
}
